/**
 * Side Cart functionality
 */

class SideCart {
  constructor() {
    this.cart = document.getElementById('sideCart');
    this.overlay = document.getElementById('sideCartOverlay');
    this.closeBtn = document.getElementById('sideCartClose');
    this.body = document.getElementById('sideCartBody');
    this.footer = document.getElementById('sideCartFooter');

    this.init();
  }

  init() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.cart.classList.contains('active')) {
        this.close();
      }
    });

    // Listen for cart button clicks
    const cartButtons = document.querySelectorAll('.cart-btn, .header-btn[href="/cart"]');
    cartButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Intercept add to cart forms
    this.interceptAddToCart();
  }

  interceptAddToCart() {
    // Only intercept product page forms, not featured products (they have their own handler)
    document.addEventListener('submit', async (e) => {
      if (e.target.matches('.product-form') && !e.target.matches('.featured-product-form')) {
        e.preventDefault();

        const form = e.target;
        const submitBtn = form.querySelector('button[type="submit"]');
        const originalText = submitBtn.innerHTML;

        // Show loading state
        submitBtn.disabled = true;
        submitBtn.innerHTML = 'Pridedama...';

        try {
          const formData = new FormData(form);
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            const data = await response.json();

            // Show success state briefly
            submitBtn.innerHTML = '✓ Pridėta';
            submitBtn.classList.add('success');

            // Open side cart and refresh
            await this.refresh();
            this.open();

            // Reset button after delay
            setTimeout(() => {
              submitBtn.disabled = false;
              submitBtn.innerHTML = originalText;
              submitBtn.classList.remove('success');
            }, 2000);
          } else {
            throw new Error('Failed to add item to cart');
          }
        } catch (error) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = 'Klaida';
          setTimeout(() => {
            submitBtn.innerHTML = originalText;
          }, 2000);
          alert('Nepavyko pridėti produkto į krepšelį. Prašome bandyti dar kartą.');
        }
      }
    });

    // Listen for custom event from featured products
    document.addEventListener('product-added-to-cart', async () => {
      await this.refresh();
      this.open();
    });
  }

  async open() {
    // Refresh cart data first
    await this.refresh();

    // Show cart
    this.cart.classList.add('active');
    this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.cart.classList.remove('active');
    this.overlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  async refresh() {
    try {
      // Show loading state
      this.body.innerHTML = `
        <div class="side-cart-loading">
          <div class="loading-spinner"></div>
          <p>Kraunama...</p>
        </div>
      `;

      // Fetch cart data
      const response = await fetch('/cart.js');

      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const cart = await response.json();

      // Get shipping config to identify shipping products
      const config = window.shippingConfig || {};
      const allShippingVariantIds = config.shippingVariantIds ?
        Object.values(config.shippingVariantIds).filter(id => id && id !== 0) : [];

      // Count only regular products (exclude shipping)
      const regularItemCount = cart.items.filter(item => {
        const isShippingProduct = item.product_type === 'Shipping' ||
                                  item.handle === 'shipping' ||
                                  allShippingVariantIds.includes(item.variant_id);
        return !isShippingProduct;
      }).reduce((total, item) => total + item.quantity, 0);

      // Update cart count in header (only regular items)
      this.updateCartCount(regularItemCount);

      // Render cart
      if (regularItemCount === 0) {
        this.renderEmptyCart();
      } else {
        this.renderCart(cart);
      }
    } catch (error) {
      this.body.innerHTML = `
        <div class="side-cart-empty">
          <h3>Klaida</h3>
          <p>Nepavyko užkrauti krepšelio. Prašome pabandyti dar kartą.</p>
          <button onclick="window.sideCart.refresh()" class="side-cart-empty-btn">Bandyti dar kartą</button>
        </div>
      `;
    }
  }

  updateCartCount(count) {
    const cartCountElements = document.querySelectorAll('.cart-count');
    cartCountElements.forEach(el => {
      el.textContent = count;
    });
  }

  renderEmptyCart() {
    this.body.innerHTML = `
      <div class="side-cart-empty">
        <div class="side-cart-empty-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
        </div>
        <h3>Jūsų krepšelis tuščias</h3>
        <p>Pridėkite produktų, kad galėtumėte tęsti apsipirkimą</p>
        <a href="/collections/all" class="side-cart-empty-btn">
          Tęsti apsipirkimą
        </a>
      </div>
    `;
    this.footer.innerHTML = '';

    // Add event listener for continue shopping button
    const continueBtn = this.body.querySelector('.side-cart-empty-btn');
    if (continueBtn) {
      continueBtn.addEventListener('click', () => this.close());
    }
  }

  renderCart(cart) {
    // Get shipping config to identify shipping products
    const config = window.shippingConfig || {};
    const allShippingVariantIds = config.shippingVariantIds ?
      Object.values(config.shippingVariantIds).filter(id => id && id !== 0) : [];

    // Filter out shipping products from display - they should only be managed from cart page
    const regularItems = cart.items.filter(item => {
      const isShippingProduct = item.product_type === 'Shipping' ||
                                item.handle === 'shipping' ||
                                allShippingVariantIds.includes(item.variant_id);
      return !isShippingProduct;
    });

    // Calculate subtotal without shipping products
    const regularItemsTotal = regularItems.reduce((total, item) => total + item.line_price, 0);

    // Render items (only regular products, not shipping)
    const itemsHTML = regularItems.map(item => this.renderItem(item)).join('');

    this.body.innerHTML = `
      <div class="side-cart-items">
        ${itemsHTML}
      </div>
    `;

    // Render footer
    this.footer.innerHTML = `
      <div class="side-cart-subtotal">
        <span class="side-cart-subtotal-label">Tarpinė suma:</span>
        <span class="side-cart-subtotal-amount">${this.formatMoney(regularItemsTotal)}</span>
      </div>
      <p class="side-cart-note">Pristatymas bus apskaičiuotas krepšelyje</p>
      <div class="side-cart-buttons">
        <a href="/cart" class="side-cart-view-cart-btn" id="sideCartGoToCart">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          Pereiti į krepšelį
        </a>
      </div>
    `;

    // Add event listeners for quantity changes and remove buttons
    this.addItemEventListeners();

    // Add event listener for "Go to Cart" button to clear shipping flag
    const goToCartBtn = document.getElementById('sideCartGoToCart');
    if (goToCartBtn) {
      goToCartBtn.addEventListener('click', () => {
        // Don't mark for removal when explicitly going to cart from side cart
        sessionStorage.removeItem('shouldRemoveShipping');
        console.log('Going to cart page from side cart, cleared removal flag');
      });
    }
  }

  renderItem(item) {
    const imageUrl = item.image ? item.image : '';
    const variantTitle = item.variant_title !== 'Default Title' ? item.variant_title : '';

    return `
      <div class="side-cart-item" data-key="${item.key}">
        <div class="side-cart-item-image">
          ${imageUrl ? `<img src="${imageUrl}" alt="${item.product_title}">` : `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
          `}
        </div>
        <div class="side-cart-item-details">
          <h4 class="side-cart-item-title">${item.product_title}</h4>
          ${variantTitle ? `<p class="side-cart-item-variant">${variantTitle}</p>` : ''}
          <p class="side-cart-item-price">${this.formatMoney(item.price)}</p>
          <div class="side-cart-item-quantity">
            <button class="side-cart-qty-btn qty-decrease" data-key="${item.key}">−</button>
            <input type="number" class="side-cart-qty-input" value="${item.quantity}" min="0" readonly>
            <button class="side-cart-qty-btn qty-increase" data-key="${item.key}">+</button>
          </div>
        </div>
        <div class="side-cart-item-actions">
          <p class="side-cart-item-total">${this.formatMoney(item.line_price)}</p>
          <button class="side-cart-item-remove" data-key="${item.key}" aria-label="Remove item">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  addItemEventListeners() {
    // Quantity decrease
    document.querySelectorAll('.qty-decrease').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const key = e.currentTarget.dataset.key;
        const item = this.cart.querySelector(`[data-key="${key}"]`);
        const input = item.querySelector('.side-cart-qty-input');
        const currentQty = parseInt(input.value);

        if (currentQty > 1) {
          await this.updateQuantity(key, currentQty - 1);
        } else {
          await this.removeItem(key);
        }
      });
    });

    // Quantity increase
    document.querySelectorAll('.qty-increase').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const key = e.currentTarget.dataset.key;
        const item = this.cart.querySelector(`[data-key="${key}"]`);
        const input = item.querySelector('.side-cart-qty-input');
        const currentQty = parseInt(input.value);

        await this.updateQuantity(key, currentQty + 1);
      });
    });

    // Remove button
    document.querySelectorAll('.side-cart-item-remove').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const key = e.currentTarget.dataset.key;
        await this.removeItem(key);
      });
    });
  }

  async updateQuantity(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (response.ok) {
        await this.refresh();
      } else {
        alert('Nepavyko atnaujinti kiekio. Prašome bandyti dar kartą.');
      }
    } catch (error) {
      alert('Nepavyko atnaujinti kiekio. Prašome bandyti dar kartą.');
    }
  }

  async removeItem(key) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      });

      if (response.ok) {
        await this.refresh();
      } else {
        alert('Nepavyko pašalinti prekės. Prašome bandyti dar kartą.');
      }
    } catch (error) {
      alert('Nepavyko pašalinti prekės. Prašome bandyti dar kartą.');
    }
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `€${amount}`;
  }
}

// Initialize side cart when DOM is ready and make it globally available
window.sideCart = null;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.sideCart = new SideCart();
  });
} else {
  window.sideCart = new SideCart();
}
